import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import Modal from 'react-modal';

import 'react-calendar/dist/Calendar.css';
import './App.css';

Modal.setAppElement('#root');

const PRESET_COLORS = [
  { hex: '#e63946', label: 'Важно' },
  { hex: '#f4a261', label: 'В работе' },
  { hex: '#457b9d', label: 'Отдых' },
  { hex: '#9b5de5', label: 'Личное' },
  { hex: '#2a9d8f', label: 'Свободно' }
];

const App = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]); // Спилок задач на выбранный день
  const [allBookings, setAllBookings] = useState([]); // Вообще все задачи для календаря
  const [dayColors, setDayColors] = useState({}); // Цвета дней
  const [selectedColor, setSelectedColor] = useState(''); 
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [newBooking, setNewBooking] = useState({
    start_time: '09:00',
    end_time: '10:00',
    description: '',
    name: ''
  });

  // Хелпер форматирования даты
  const formatDateString = (date) => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().split('T')[0];
  };

  // Инициализация: загрузка данных из локальной памяти устройства
  useEffect(() => {
    const savedBookings = localStorage.getItem('local_bookings');
    const savedColors = localStorage.getItem('local_day_colors');

    if (savedBookings) setAllBookings(JSON.parse(savedBookings));
    if (savedColors) setDayColors(JSON.parse(savedColors));
  }, []);

  // Фильтрация задач при смене выбранного дня или обновлении общего списка
  useEffect(() => {
    const dateString = formatDateString(selectedDate);
    const filtered = allBookings.filter(b => b.booking_date === dateString);
    // Сортируем задачи по времени начала
    filtered.sort((a, b) => a.start_time.localeCompare(b.start_time));
    setBookings(filtered);
  }, [selectedDate, allBookings]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dateString = formatDateString(date);
    
    setSelectedColor(dayColors[dateString] || '');
    setErrorMessage('');
    setNewBooking({ start_time: '09:00', end_time: '10:00', description: '', name: '' });
    setModalIsOpen(true);
  };

  // Переключение и сохранение цвета дня
  const selectPresetColor = (hex) => {
    const dateString = formatDateString(selectedDate);
    const nextColor = selectedColor === hex ? '' : hex; 
    setSelectedColor(nextColor);

    const updatedColors = { ...dayColors, [dateString]: nextColor };
    // Если цвет пустой, удаляем свойство из объекта, чтобы не копился мусор
    if (!nextColor) delete updatedColors[dateString];

    setDayColors(updatedColors);
    localStorage.setItem('local_day_colors', JSON.stringify(updatedColors));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBooking({ ...newBooking, [name]: value });
  };

  // Добавление новой почасовой задачи с проверкой пересечений
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    const dateString = formatDateString(selectedDate);

    if (newBooking.end_time <= newBooking.start_time) {
      setErrorMessage("Время окончания должно быть позже времени начала.");
      return;
    }

    // Локальная проверка на наложение временных слотов (то, что раньше делал бэкенд)
    const hasOverlap = allBookings.some(b => 
      b.booking_date === dateString &&
      b.start_time < newBooking.end_time &&
      b.end_time > newBooking.start_time
    );

    if (hasOverlap) {
      setErrorMessage("Это время уже занято в расписании.");
      return;
    }

    const newEntry = {
      id: Date.now(), // Уникальный локальный ID на основе таймстампа
      booking_date: dateString,
      start_time: newBooking.start_time,
      end_time: newBooking.end_time,
      name: newBooking.name || null,
      description: newBooking.description || null
    };

    const updatedBookings = [...allBookings, newEntry];
    setAllBookings(updatedBookings);
    localStorage.setItem('local_bookings', JSON.stringify(updatedBookings));

    // Очищаем форму
    setNewBooking({ ...newBooking, description: '', name: '' });
  };

  // Удаление задачи
  const handleDelete = (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить эту задачу?")) return;
    
    const updatedBookings = allBookings.filter(b => b.id !== id);
    setAllBookings(updatedBookings);
    localStorage.setItem('local_bookings', JSON.stringify(updatedBookings));
  };

  const getTileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateString = formatDateString(date);
      const customColor = dayColors[dateString];
      if (customColor && customColor !== '') {
        return `custom-tile-${customColor.replace('#', '')}`;
      }
    }
    return null;
  };

  return (
    <div className="app-container">
      <h1></h1>
      <Calendar 
        onChange={setSelectedDate} 
        value={selectedDate} 
        onClickDay={handleDateClick} 
        tileClassName={getTileClassName}
      />
      
      <Modal 
        isOpen={modalIsOpen} 
        onRequestClose={() => setModalIsOpen(false)}
        overlayClassName="modal-overlay"
        className="modal-content-wide"
      >
        <div className="planner-layout">
          
          <div className="planner-list-section">
            <div className="title-color-section">
              <h2>Планы на {formatDateString(selectedDate)}</h2>
              
              <div className="palette-container">
                <span className="palette-label">Категория дня:</span>
                <div className="palette-circles">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color.hex}
                      type="button"
                      className={`palette-circle ${selectedColor === color.hex ? 'active' : ''}`}
                      style={{ backgroundColor: color.hex }}
                      onClick={() => selectPresetColor(color.hex)}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            {bookings.length === 0 ? (
              <p className="no-bookings">На этот день задач пока нет.</p>
            ) : (
              <div className="bookings-timeline">
                {bookings.map((item) => (
                  <div key={item.id} className="timeline-card">
                    <div className="card-time">
                      <strong>{item.start_time} - {item.end_time}</strong>
                    </div>
                    <div className="card-body">
                      <h3 className="event-title">{item.name || "Без названия"}</h3>
                      {item.description && <p className="client-desc"><strong>Инфо:</strong> {item.description}</p>}
                    </div>
                    <button className="btn-delete" onClick={() => handleDelete(item.id)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="planner-form-section">
            <h2>Добавить задачу</h2>
            {errorMessage && <div className="error-banner">{errorMessage}</div>}
            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Время начала</label>
                  <input type="time" name="start_time" value={newBooking.start_time} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Время окончания</label>
                  <input type="time" name="end_time" value={newBooking.end_time} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="form-group">
                <label>Название / Событие</label>
                <input type="text" name="name" value={newBooking.name} onChange={handleInputChange} placeholder="..." />
              </div>
              <div className="form-group">
                <label>Описание / Заметка</label>
                <textarea name="description" value={newBooking.description} onChange={handleInputChange} placeholder="Детали..."></textarea>
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Добавить в расписание</button>
                <button type="button" className="btn btn-secondary" onClick={() => setModalIsOpen(false)}>Закрыть</button>
              </div>
            </form>
          </div>

        </div>
      </Modal>
    </div>
  );
};

export default App;
